import React from "react"
import dayjs from "dayjs"

// styling
import styled from "@emotion/styled"

// components
import MenuItemTooltipContent from "components/MenuItemTooltipContent"
import MenuItem from "components/MenuItem"
import MenuItemModal from "modals/MenuItem"
import {
  Space,
  Typography,
  Collapse,
  List,
  Divider,
  Input,
  Tooltip,
  Avatar,
  message,
  Tag,
  Badge,
} from "antd"
import { PlusOutlined, SearchOutlined } from "@ant-design/icons"

// configs
import config from "config"

// store
import { useFunnel } from "store/funnel"
import { useMenu } from "store/menu"
import { useResto } from "store/resto"
import { useTranslation } from "react-i18next"

// utils
import { matchTimeRanges } from "utils"
import { debounce, deburr } from "lodash-es"
import { useOrders } from "store/orders"
import { assembleMenuItems } from "utils/menu"
import MenuFame from "./MenuFame"

const Menu = styled.div`
  min-height: 300px;

  .ant-collapse {
    -moz-column-width: 500px;
    -webkit-column-width: 500px;
    column-width: 500px;

    @media (max-width: 1920px) {
      -moz-column-width: 400px;
      -webkit-column-width: 400px;
      column-width: 400px;
    }

    @media (max-width: 1080px) {
      -moz-column-width: 350px;
      -webkit-column-width: 350px;
      column-width: 350px;
    }

    @media (max-width: 820px) {
      -moz-column-width: 300px;
      -webkit-column-width: 300px;
      column-width: 300px;
    }

    column-fill: balance;

    .ant-collapse-header {
      background-color: rgba(0, 0, 0, 0.1);
      // background-color: white;
      margin: 0;
      margin-bottom: 4px;
      border-radius: 2px;

      padding: 8px !important;

      &:hover {
        background-color: rgba(0, 0, 0, 0.2);
      }
    }

    .ant-collapse-item-active .ant-collapse-header {
      background-color: #3b3b3b;
      color: white;
      margin-bottom: 0px;
      border-bottom: 1px solid lightgrey;

      * {
        color: white;
      }
    }

    .ant-collapse-item {
      &:not(:first-of-type) {
        margin-top: 8px;
      }
    }

    .ant-collapse-content-box {
      padding: 0 !important;
      margin: 0;
      margin-bottom: 4px;

      background-color: #4b4b4b;
    }

    .ant-list-item {
      background-color: transparent;
      margin-bottom: 2px;
      border-bottom: none;
      cursor: pointer;

      .mobile-extra {
        display: none;
      }

      border-top: 1px dashed lightgrey;

      @media (max-width: 820px) {
        display: block;
        .mobile-extra {
          display: block;
        }
      }

      color: ${true ? "white" : config.textColor};

      * {
        color: ${true ? "white" : config.textColor};
      }

      svg {
        opacity: 0;

        @media (max-width: 820px) {
          opacity: 1;
          display: none;
        }
      }

      &:hover,
      &:focus {
        background-color: rgba(255, 255, 255, 0.2);

        svg {
          opacity: 1;
        }
      }
    }
  }
`

const Speisekarte = ({ onAdd }) => {
  const [search, setSearch] = React.useState("")
  const [activeKey, setActiveKey] = React.useState([])
  const { menu, status } = useMenu()
  const [selectedMenuItem, setselectedMenuItem] = React.useState(null)
  const restoSettings = useResto()
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage
  const funnel = useFunnel()
  const { order } = useOrders()

  const [messageApi, contextHolder] = message.useMessage()

  if (status === "loading") {
    return (
      <Divider>
        <Tag>{t("menu_loading")}</Tag>
      </Divider>
    )
  }

  if (!menu || (menu || []).map((cat) => cat.items).flat().length === 0)
    return (
      <Divider>
        <Tag>{t("no_menu_available")}</Tag>
      </Divider>
    )

  const restoMenu = menu
    .filter((cat) => !cat.hidden)
    .map((cat) => ({
      ...cat,
      items: cat.items
        .filter((item) => !item.hidden)
        .map((item) => ({
          ...item,
        })),
    }))

  const debouncedSearch = debounce((value) => {
    setSearch(value)
  }, 1000)

  const filteredMenu = restoMenu
    .map((category) => ({
      ...category,
      items: [
        ...category.items.filter(
          (i) =>
            search === "" ||
            deburr(category.label)
              .toLowerCase()
              .includes(search.toLowerCase()) ||
            deburr(category.key).toLowerCase().includes(search.toLowerCase()) ||
            deburr(i.name).toLowerCase().includes(search.toLowerCase()) ||
            `${i.id}`.toLowerCase().includes(search.toLowerCase()) ||
            deburr(`${i.info || ""}`)
              .toLowerCase()
              .includes(search.toLowerCase())
        ),
      ],
    }))
    .filter((category) => category.items.length > 0)

  return (
    <>
      {contextHolder}

      <MenuItemModal
        open={selectedMenuItem !== null}
        onCancel={() => setselectedMenuItem(null)}
        item={selectedMenuItem}
        onAdd={(item, amount) => {
          onAdd(item, amount)

          messageApi.success({
            key: "add",
            content: t("added_to_order", { name: item.name }),
            duration: 2,
          })
          setselectedMenuItem(null)
        }}
      />

      <MenuFame
        onSelect={(item) => {
          setselectedMenuItem(item)
        }}
      />

      <Divider
        orientation="left"
        // style={{
        //   borderTopColor: config.textColor,
        // }}
      >
        <Space>
          <Input
            data-testid="search"
            prefix={<SearchOutlined />}
            placeholder={t("search_placeholder")}
            onChange={(e) => {
              debouncedSearch(e.target.value)
            }}
            style={{ width: 300, marginTop: 10, marginBottom: 24 }}
          />
        </Space>
      </Divider>

      {filteredMenu.length === 0 && (
        <Typography.Text>{t("no_menu_items_found")}</Typography.Text>
      )}

      <Menu>
        <Collapse
          data-testid="menu"
          accordion={search === ""}
          ghost
          onChange={(v) => {
            setActiveKey(v)

            funnel.add("interact_category")
          }}
          activeKey={
            search !== "" ? restoMenu.map((cat) => cat.key) : activeKey
          }
          items={filteredMenu.map((category) => {
            const noOrderDay =
              !category.times ||
              Object.values(category.times).every((item) => item === null)
                ? false
                : !category.times[new Date().getDay()]
            const outOfOrder =
              !category.times ||
              Object.values(category.times).every((item) => item === null)
                ? false
                : category.times && !matchTimeRanges(category.times)

            const orderable = !noOrderDay && !outOfOrder

            const HeaderLabel = () => (
              <Space direction="vertical" data-testid="category">
                <Space>
                  <Avatar
                    src={`icons/${category.icon}.png`}
                    size={24}
                    alt={`icon-${category.icon}`}
                  />

                  <Typography.Text strong>
                    {category.label_i18n?.[lang] ?? category.label}
                  </Typography.Text>
                </Space>
                {!orderable &&
                  (noOrderDay ? (
                    <Typography.Text italic>
                      {t("cannot_order_today")}
                    </Typography.Text>
                  ) : (
                    <Typography.Text italic>
                      {t("cannot_order_now")}{" "}
                      {category.times[new Date().getDay()]
                        .map(
                          (item) =>
                            `${dayjs(item.start_time).format(
                              "HH:mm"
                            )} - ${dayjs(item.end_time).format("HH:mm")}`
                        )
                        .join(", ")}
                    </Typography.Text>
                  ))}
              </Space>
            )

            return {
              key: category.key,
              label:
                activeKey.length === 0 &&
                restoSettings &&
                menu &&
                restoSettings.popular &&
                restoSettings.popular.categories.find(
                  (item) => item.category_id === category.key
                ) ? (
                  <Badge.Ribbon
                    text={t("popular_badge")}
                    size="small"
                    color="rgba(0, 0, 0, 0.7)"
                  >
                    <HeaderLabel />
                  </Badge.Ribbon>
                ) : (
                  <HeaderLabel />
                ),
              children: (
                <List
                  size="small"
                  dataSource={category.items}
                  renderItem={(item) => {
                    const foodItem = assembleMenuItems(item, category)

                    const Item = () => (
                      <List.Item
                        data-testid="menu-item"
                        onClick={() => {
                          if (!orderable) {
                            messageApi.error({
                              key: "add",
                              content: t("cannot_order_outside_time", {
                                name: item.name,
                              }),
                              duration: 5,
                            })
                            return
                          }

                          setselectedMenuItem(foodItem)
                        }}
                      >
                        <MenuItem item={foodItem} />

                        <PlusOutlined />
                      </List.Item>
                    )

                    const orderCount = order.list.filter(
                      (orderItem) => orderItem.id === foodItem.id
                    ).length

                    return (
                      <Tooltip
                        zIndex={1}
                        trigger={["hover"]}
                        placement="right"
                        color="#4b4b4b"
                        title={<MenuItemTooltipContent item={foodItem} />}
                      >
                        {/* add null, otherwise tooltip is not triggered */}
                        {null}
                        {orderCount > 0 ? (
                          <Badge.Ribbon
                            text={t("ordered_badge", { count: orderCount })}
                            size="small"
                          >
                            <Item />
                          </Badge.Ribbon>
                        ) : restoSettings &&
                          menu &&
                          restoSettings.popular &&
                          restoSettings.popular.items.find(
                            (item) => item.item_id === foodItem.id
                          ) ? (
                          <Badge.Ribbon
                            text={t("topseller_badge")}
                            size="small"
                            color="red"
                          >
                            <Item />
                          </Badge.Ribbon>
                        ) : (
                          <Item />
                        )}
                      </Tooltip>
                    )
                  }}
                />
              ),
            }
          })}
        />
      </Menu>
    </>
  )
}

export default Speisekarte
